/**
 * Frame Restyler Test Page - Post-process videos to clean 2D cartoon style
 * 
 * Pipeline: Video → Extract Frames → Lineart → SDXL ControlNet Restyle → Clean Video
 * Removes AI artifacts and applies consistent 2D cartoon styling.
 * Access via: /frame-restyler (secret, unlisted route)
 */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Slider } from "@/components/ui/slider";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { 
  Zap, Loader2, Film, Layers,
  Upload, Trash2, Play, Pause,
  Sparkles, Check, X, AlertTriangle, Settings2,
  Download, Video, ArrowLeft, Eye, EyeOff, Info, Paintbrush, RefreshCw
} from "lucide-react";
import api from "@/config/axiosInstance";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";

const getAssetUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/api/")) return `${API}${url}`;
  return url;
};

// Style icons and colors
const STYLE_CONFIG = {
  "2d_cartoon": { icon: "🎨", color: "bg-orange-500", label: "2D Cartoon" },
  "anime": { icon: "⭐", color: "bg-pink-500", label: "Anime" },
  "comic_book": { icon: "💥", color: "bg-yellow-500", label: "Comic Book" },
  "watercolor": { icon: "🖌️", color: "bg-blue-500", label: "Watercolor" },
  "sketch": { icon: "✏️", color: "bg-gray-500", label: "Sketch" },
  "pixel_art": { icon: "👾", color: "bg-green-500", label: "Pixel Art" },
  "custom": { icon: "⚙️", color: "bg-purple-500", label: "Custom" }
};

export default function FrameRestyler() {
  const navigate = useNavigate();
  
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Form state
  const [videoUrl, setVideoUrl] = useState("");
  const [style, setStyle] = useState("2d_cartoon");
  const [customPrompt, setCustomPrompt] = useState("");
  const [negativePrompt, setNegativePrompt] = useState("blurry, morphing, distorted, low quality, artifacts, 3D render, photorealistic");
  const [controlnetStrength, setControlnetStrength] = useState(0.7);  // Style strength (0.3-0.95)
  const [guidanceScale, setGuidanceScale] = useState(7.5);
  const [inferenceSteps, setInferenceSteps] = useState(25);
  const [outputFps, setOutputFps] = useState(24);
  
  // Upload ref
  const videoUploadRef = useRef(null);
  
  // Video preview state
  const [videoPreviewPlaying, setVideoPreviewPlaying] = useState(false);
  const videoPreviewRef = useRef(null);
  
  // Generation state
  const [processing, setProcessing] = useState(false);
  const [jobStatus, setJobStatus] = useState(null);
  const [jobHistory, setJobHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  const pollRef = useRef(null);

  useEffect(() => {
    loadConfig();
    loadJobHistory();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const loadConfig = async () => {
    try {
      const response = await api.get("/frame-restyler/config");
      setConfig(response.data);
    } catch (error) {
      console.error("Failed to load config:", error);
      toast.error("Failed to load configuration");
    } finally {
      setLoading(false);
    }
  };

  const loadJobHistory = async () => {
    try {
      const response = await api.get("/frame-restyler/jobs?limit=10");
      setJobHistory(response.data.jobs || []);
    } catch (error) {
      console.error("Failed to load job history:", error);
    }
  };

  const uploadVideo = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await api.post("/uploads/temp", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      return response.data.url;
    } catch (error) {
      console.error("Upload error:", error);
      return URL.createObjectURL(file);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      toast.error("Video too large. Maximum 50MB.");
      return;
    }
    
    toast.loading("Uploading video...");
    const url = await uploadVideo(file);
    setVideoUrl(url);
    toast.dismiss();
    toast.success("Video uploaded");
  };

  const toggleVideoPreview = () => {
    if (videoPreviewRef.current) {
      if (videoPreviewPlaying) {
        videoPreviewRef.current.pause();
      } else {
        videoPreviewRef.current.play();
      }
      setVideoPreviewPlaying(!videoPreviewPlaying);
    }
  };

  const handleRestyle = async () => {
    if (!videoUrl) {
      toast.error("Please upload a video first");
      return;
    }
    
    if (style === "custom" && !customPrompt.trim()) {
      toast.error("Please enter a custom style prompt");
      return;
    }
    
    setProcessing(true);
    setJobStatus(null);
    
    try {
      const response = await api.post("/frame-restyler/restyle", {
        video_url: videoUrl,
        style,
        custom_prompt: style === "custom" ? customPrompt.trim() : null,
        negative_prompt: negativePrompt,
        controlnet_strength: controlnetStrength,
        guidance_scale: guidanceScale,
        num_inference_steps: inferenceSteps,
        output_fps: outputFps
      });
      
      if (response.data.success) {
        toast.success("Frame restyle started!");
        startPolling(response.data.job_id);
      } else {
        throw new Error(response.data.message || "Failed to start restyle");
      }
    } catch (error) {
      console.error("Restyle error:", error);
      toast.error(error.response?.data?.detail || error.message || "Failed to start restyle");
      setProcessing(false);
    }
  };

  const startPolling = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    
    const poll = async () => {
      try {
        const response = await api.get(`/frame-restyler/status/${jobId}`);
        setJobStatus(response.data);
        
        if (response.data.status === "completed") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setProcessing(false);
          toast.success("Frame restyle complete!");
          loadJobHistory();
        } else if (response.data.status === "failed") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setProcessing(false);
          toast.error(response.data.error_message || "Restyle failed");
          loadJobHistory();
        }
      } catch (error) {
        console.error("Polling error:", error);
      }
    };
    
    poll();
    pollRef.current = setInterval(poll, 2000);
  };

  const handleDeleteJob = async (jobId) => {
    try {
      await api.delete(`/frame-restyler/jobs/${jobId}`);
      setJobHistory(prev => prev.filter(j => j.job_id !== jobId));
      toast.success("Job deleted");
    } catch (error) {
      toast.error("Failed to delete job");
    }
  };

  const handleDownloadVideo = async (videoUrl, jobId) => {
    try {
      const response = await fetch(getAssetUrl(videoUrl));
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `restyled_${jobId.slice(0, 8)}.mp4`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast.error("Failed to download video");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-zinc-950 text-white">
        {/* Header */}
        <header className="border-b border-zinc-800 bg-zinc-900/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(-1)}
                className="text-zinc-400 hover:text-white"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <Layers className="w-5 h-5 text-cyan-400" />
                <h1 className="text-lg font-semibold">Frame Restyler</h1>
                <Badge variant="outline" className="text-cyan-400 border-cyan-400/50 text-xs">
                  Secret
                </Badge>
              </div>
            </div>
            <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/50">
              SDXL + ControlNet
            </Badge>
          </div>
        </header>

        <main className="max-w-7xl mx-auto p-4 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel */}
          <div className="lg:col-span-2 space-y-6">
            {/* Info Banner */}
            <Card className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border-cyan-500/30">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-cyan-400 mt-0.5 shrink-0" />
                  <div className="text-sm text-zinc-300 space-y-1">
                    <p className="font-medium text-cyan-300">Remove AI Artifacts, Apply Clean 2D Style</p>
                    <p className="text-zinc-400 text-xs">
                      Upload any video (from Kling, Dreamactor, etc.) → We extract lineart from each frame → 
                      SDXL + ControlNet restyles while preserving structure → Output clean 2D cartoon animation.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pipeline Visualization */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between text-xs">
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center">
                      <Video className="w-5 h-5 text-zinc-400" />
                    </div>
                    <span className="text-zinc-500">Input</span>
                  </div>
                  <div className="flex-1 h-px bg-gradient-to-r from-zinc-700 via-cyan-500/50 to-zinc-700 mx-2" />
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center">
                      <Layers className="w-5 h-5 text-cyan-400" />
                    </div>
                    <span className="text-zinc-500">Lineart</span>
                  </div>
                  <div className="flex-1 h-px bg-gradient-to-r from-zinc-700 via-cyan-500/50 to-zinc-700 mx-2" />
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center">
                      <Paintbrush className="w-5 h-5 text-cyan-400" />
                    </div>
                    <span className="text-zinc-500">Restyle</span>
                  </div>
                  <div className="flex-1 h-px bg-gradient-to-r from-zinc-700 via-cyan-500/50 to-zinc-700 mx-2" />
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center border border-cyan-500/30">
                      <Film className="w-5 h-5 text-cyan-400" />
                    </div>
                    <span className="text-cyan-400">Clean</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Video Upload */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Video className="w-4 h-4 text-cyan-400" />
                  Input Video
                </CardTitle>
                <CardDescription className="text-xs text-zinc-500">
                  Upload the video you want to restyle (max 15 seconds, 50MB)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div 
                  className="aspect-video rounded-lg border border-dashed border-zinc-700 bg-zinc-800/50 flex items-center justify-center overflow-hidden cursor-pointer hover:border-cyan-500/50 transition-colors relative"
                  onClick={() => !videoUrl && videoUploadRef.current?.click()}
                  data-testid="video-upload-area"
                >
                  {videoUrl ? (
                    <>
                      <video 
                        ref={videoPreviewRef}
                        src={getAssetUrl(videoUrl)} 
                        className="w-full h-full object-contain"
                        loop
                        muted
                        onPlay={() => setVideoPreviewPlaying(true)}
                        onPause={() => setVideoPreviewPlaying(false)}
                      />
                      <Button
                        variant="secondary"
                        size="icon"
                        className="absolute bottom-3 right-3 h-10 w-10 bg-black/70 hover:bg-black/90"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleVideoPreview();
                        }}
                      >
                        {videoPreviewPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                      </Button>
                    </>
                  ) : (
                    <div className="text-center p-6">
                      <Upload className="w-10 h-10 mx-auto text-zinc-500 mb-3" />
                      <p className="text-sm text-zinc-400">Click to upload video</p>
                      <p className="text-xs text-zinc-600 mt-1">MP4, MOV • Max 15 sec • 50MB</p>
                    </div>
                  )}
                </div>
                <input 
                  ref={videoUploadRef} 
                  type="file" 
                  accept="video/*" 
                  className="hidden" 
                  onChange={handleVideoUpload} 
                />
                {videoUrl && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="w-full mt-2 text-xs text-zinc-400 hover:text-red-400" 
                    onClick={() => {
                      setVideoUrl("");
                      setVideoPreviewPlaying(false);
                    }}
                  >
                    <Trash2 className="w-3 h-3 mr-1" /> Remove Video
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Style Selection */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Paintbrush className="w-4 h-4 text-cyan-400" />
                  Output Style
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {Object.entries(STYLE_CONFIG).map(([key, cfg]) => (
                    <Button
                      key={key}
                      variant={style === key ? "default" : "outline"}
                      size="sm"
                      className={`h-auto py-3 flex flex-col gap-1 ${
                        style === key 
                          ? `${cfg.color} hover:opacity-90` 
                          : "border-zinc-700 hover:border-zinc-500"
                      }`}
                      onClick={() => setStyle(key)}
                      disabled={processing}
                      data-testid={`style-${key}`}
                    >
                      <span className="text-lg">{cfg.icon}</span>
                      <span className="text-xs">{cfg.label}</span>
                    </Button>
                  ))}
                </div>
                
                {/* Custom Prompt */}
                {style === "custom" && (
                  <div className="space-y-2">
                    <Label className="text-xs text-zinc-400">Custom Style Prompt *</Label>
                    <Textarea
                      placeholder="Describe your desired style... e.g., 'Studio Ghibli style, soft watercolors, dreamy atmosphere'"
                      value={customPrompt}
                      onChange={(e) => setCustomPrompt(e.target.value)}
                      className="min-h-[80px] bg-zinc-800/50 border-zinc-700 text-sm"
                      disabled={processing}
                      data-testid="custom-prompt-input"
                    />
                  </div>
                )}
                
                {/* Show selected style description */}
                {style !== "custom" && config?.style_descriptions?.[style] && (
                  <div className="p-3 bg-zinc-800/30 rounded-lg">
                    <p className="text-xs text-zinc-400">
                      <span className="text-cyan-400 font-medium">Style prompt: </span>
                      {config.style_descriptions[style]}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Settings */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Settings2 className="w-4 h-4 text-cyan-400" />
                    Processing Settings
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="text-xs text-zinc-400"
                  >
                    {showAdvanced ? "Hide" : "Show"} Advanced
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Output FPS */}
                <div className="space-y-2">
                  <Label className="text-xs text-zinc-400">Output Frame Rate</Label>
                  <Select value={outputFps.toString()} onValueChange={(v) => setOutputFps(parseInt(v))} disabled={processing}>
                    <SelectTrigger className="bg-zinc-800/50 border-zinc-700 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="12">12 FPS (Fast, choppy)</SelectItem>
                      <SelectItem value="24">24 FPS (Cinematic)</SelectItem>
                      <SelectItem value="30">30 FPS (Smooth)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Style Strength (was ControlNet Strength) */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-zinc-400 flex items-center gap-1">
                      Style Strength
                      <Tooltip>
                        <TooltipTrigger>
                          <Info className="w-3 h-3" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-xs">How much to change from original (higher = more stylization)</p>
                        </TooltipContent>
                      </Tooltip>
                    </Label>
                    <span className="text-xs font-medium text-cyan-400">{controlnetStrength.toFixed(1)}</span>
                  </div>
                  <Slider
                    value={[controlnetStrength]}
                    onValueChange={([v]) => setControlnetStrength(v)}
                    min={0.3}
                    max={0.95}
                    step={0.05}
                    disabled={processing}
                    className="py-2"
                  />
                  <div className="flex justify-between text-[10px] text-zinc-600">
                    <span>Subtle</span>
                    <span>Dramatic</span>
                  </div>
                </div>

                {/* Advanced Settings */}
                {showAdvanced && (
                  <div className="space-y-4 pt-2 border-t border-zinc-800">
                    {/* Guidance Scale */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-zinc-400">Style Strength</Label>
                        <span className="text-xs font-medium text-cyan-400">{guidanceScale}</span>
                      </div>
                      <Slider
                        value={[guidanceScale]}
                        onValueChange={([v]) => setGuidanceScale(v)}
                        min={1}
                        max={20}
                        step={0.5}
                        disabled={processing}
                        className="py-2"
                      />
                    </div>

                    {/* Inference Steps */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-zinc-400">Quality (Steps)</Label>
                        <span className="text-xs font-medium text-cyan-400">{inferenceSteps}</span>
                      </div>
                      <Slider
                        value={[inferenceSteps]}
                        onValueChange={([v]) => setInferenceSteps(v)}
                        min={10}
                        max={50}
                        step={5}
                        disabled={processing}
                        className="py-2"
                      />
                    </div>

                    {/* Negative Prompt */}
                    <div className="space-y-2">
                      <Label className="text-xs text-zinc-400">Negative Prompt</Label>
                      <Textarea
                        placeholder="What to avoid..."
                        value={negativePrompt}
                        onChange={(e) => setNegativePrompt(e.target.value)}
                        className="min-h-[60px] bg-zinc-800/50 border-zinc-700 text-xs"
                        disabled={processing}
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Restyle Button */}
            <Button
              onClick={handleRestyle}
              disabled={processing || !videoUrl || (style === "custom" && !customPrompt.trim())}
              className="w-full bg-cyan-600 hover:bg-cyan-700 text-white py-6 text-lg font-medium"
              data-testid="restyle-btn"
            >
              {processing ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Processing Frames...
                </>
              ) : (
                <>
                  <RefreshCw className="w-5 h-5 mr-2" />
                  Restyle Video
                </>
              )}
            </Button>

            {/* Job Status */}
            {jobStatus && (
              <Card className={`border ${
                jobStatus.status === "completed" ? "border-green-500/50 bg-green-500/5" :
                jobStatus.status === "failed" ? "border-red-500/50 bg-red-500/5" :
                "border-cyan-500/50 bg-cyan-500/5"
              }`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {jobStatus.status === "completed" ? (
                        <Check className="w-4 h-4 text-green-400" />
                      ) : jobStatus.status === "failed" ? (
                        <X className="w-4 h-4 text-red-400" />
                      ) : (
                        <Loader2 className="w-4 h-4 text-cyan-400 animate-spin" />
                      )}
                      <span className="text-sm font-medium capitalize">{jobStatus.status}</span>
                      {jobStatus.total_frames > 0 && (
                        <Badge variant="outline" className="text-xs">
                          {jobStatus.processed_frames}/{jobStatus.total_frames} frames
                        </Badge>
                      )}
                    </div>
                    <span className="text-xs text-zinc-500">{Math.round(jobStatus.progress_percent)}%</span>
                  </div>
                  
                  <Progress value={jobStatus.progress_percent} className="h-2 mb-2" />
                  <p className="text-xs text-zinc-400">{jobStatus.progress_message}</p>
                  
                  {jobStatus.status === "completed" && jobStatus.video_url && (
                    <div className="mt-4">
                      <video src={getAssetUrl(jobStatus.video_url)} controls className="w-full rounded-lg bg-black" />
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full mt-2 text-xs border-green-500/50 text-green-400 hover:bg-green-500/10"
                        onClick={() => handleDownloadVideo(jobStatus.video_url, jobStatus.job_id)}
                        data-testid="download-result-btn"
                      >
                        <Download className="w-3 h-3 mr-1" /> Download Restyled Video
                      </Button>
                    </div>
                  )}
                  
                  {jobStatus.status === "failed" && jobStatus.error_message && (
                    <div className="mt-2 p-2 bg-red-500/10 rounded text-xs text-red-400">
                      {jobStatus.error_message}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Panel - History & Info */}
          <div className="space-y-4">
            {/* History */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Film className="w-4 h-4 text-cyan-400" />
                    History
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setShowHistory(!showHistory)} className="h-6 w-6 p-0">
                    {showHistory ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </CardHeader>
              {showHistory && (
                <CardContent className="p-0">
                  <ScrollArea className="h-[350px]">
                    {jobHistory.length === 0 ? (
                      <div className="p-4 text-center text-zinc-500 text-sm">No restyles yet</div>
                    ) : (
                      <div className="divide-y divide-zinc-800">
                        {jobHistory.map((job) => (
                          <div key={job.job_id} className="p-3 hover:bg-zinc-800/30 transition-colors">
                            <div className="flex items-start gap-2 mb-2">
                              <Badge variant="outline" className={`text-[10px] ${
                                job.status === "completed" ? "text-green-400 border-green-500/50" :
                                job.status === "failed" ? "text-red-400 border-red-500/50" :
                                "text-cyan-400 border-cyan-500/50"
                              }`}>
                                {job.status}
                              </Badge>
                              <Badge variant="outline" className="text-[10px] text-zinc-400">
                                {STYLE_CONFIG[job.style]?.label || job.style}
                              </Badge>
                            </div>
                            <div className="text-[10px] text-zinc-500 mb-2">
                              {new Date(job.created_at).toLocaleString()}
                              {job.total_frames > 0 && ` • ${job.total_frames} frames`}
                            </div>
                            {job.status === "completed" && job.video_url && (
                              <video src={getAssetUrl(job.video_url)} controls className="w-full rounded-lg bg-black mb-2" />
                            )}
                            <div className="flex gap-2">
                              {job.status === "completed" && job.video_url && (
                                <Button variant="ghost" size="sm" className="flex-1 text-[10px] h-7 text-green-400" onClick={() => handleDownloadVideo(job.video_url, job.job_id)}>
                                  <Download className="w-3 h-3 mr-1" /> Download
                                </Button>
                              )}
                              <Button variant="ghost" size="sm" className="text-[10px] h-7 text-red-400" onClick={() => handleDeleteJob(job.job_id)}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              )}
            </Card>

            {/* How It Works */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Info className="w-4 h-4 text-cyan-400" />
                  How It Works
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs text-zinc-500 space-y-2">
                <div className="flex items-start gap-2">
                  <span className="bg-cyan-600 text-white rounded-full w-5 h-5 flex items-center justify-center shrink-0 text-[10px]">1</span>
                  <p><strong className="text-cyan-400">Extract frames</strong> from your input video</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="bg-cyan-600 text-white rounded-full w-5 h-5 flex items-center justify-center shrink-0 text-[10px]">2</span>
                  <p><strong className="text-cyan-400">Extract lineart</strong> to preserve structure</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="bg-cyan-600 text-white rounded-full w-5 h-5 flex items-center justify-center shrink-0 text-[10px]">3</span>
                  <p><strong className="text-cyan-400">SDXL + ControlNet</strong> restyles each frame</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="bg-cyan-600 text-white rounded-full w-5 h-5 flex items-center justify-center shrink-0 text-[10px]">4</span>
                  <p><strong className="text-green-400">Clean 2D video</strong> - no morphing!</p>
                </div>
              </CardContent>
            </Card>

            {/* Tips */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  Tips
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs text-zinc-500 space-y-1">
                <p>• Works best with <strong className="text-cyan-400">short clips (5-15s)</strong></p>
                <p>• <strong className="text-cyan-400">Higher structure preservation</strong> = closer to original</p>
                <p>• Use <strong className="text-cyan-400">fewer steps</strong> for faster results</p>
                <p>• <strong className="text-cyan-400">12 FPS</strong> processes 2x faster than 24 FPS</p>
                <p>• Good for <strong className="text-cyan-400">cleaning up Kling/AI video</strong> artifacts</p>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </TooltipProvider>
  );
}
